/**
 * Helper to extract numeric ID from API Platform IRI
 * @param {string} iri - The IRI (e.g., "/api/civilites/40")
 * @returns {number|null} - The numeric ID or null if invalid
 */
export const extractIdFromIri = (iri) => {
  if (!iri || typeof iri !== "string") return null;
  const parts = iri.split("/");
  const id = parseInt(parts[parts.length - 1], 10);
  return Number.isNaN(id) ? null : id;
};

/**
 * Normalize API Platform collection data to consistent format
 * - Extracts numeric id from @id IRI and adds it as 'id' property
 * - Preserves all original properties (@id, @type, code, libelle, etc.)
 * @param {Array|Object} data - API Platform response (with member) or array
 * @returns {Array} - Normalized array with numeric 'id' property
 */
export const normalizeApiPlatformCollection = (data) => {
  const member = data?.member || data || [];
  if (!Array.isArray(member)) {
    return [];
  }
  return member.map((item) => ({
    ...item,
    id: item.id ?? extractIdFromIri(item["@id"]),
  }));
};

/**
 * Helper to create an IRI from resource type and ID
 * @param {string} resourceType - The resource type (e.g., "civilites")
 * @param {number} id - The numeric ID
 * @returns {string} - The IRI (e.g., "/api/civilites/40")
 */
export const createIri = (resourceType, id) => {
  return `/api/${resourceType}/${id}`;
};

/**
 * Helper to transform data for API Platform submission
 * Converts numeric IDs to IRIs for relations
 * @param {object} data - The form data
 * @param {object} relations - Map of field names to resource types
 * @returns {object} - Transformed data ready for API Platform
 */
export const transformForApiPlatform = (data, relations = {}) => {
  const transformed = { ...data };

  Object.entries(relations).forEach(([field, resourceType]) => {
    if (transformed[field] !== null && transformed[field] !== undefined) {
      transformed[field] = createIri(resourceType, transformed[field]);
    }
  });

  return transformed;
};
